export type BlockingGateId =
  | "panic-alert"
  | "shift-end"
  | "break-active"
  | "break-notice"
  | "briefing-forced-read";

export type BlockingGateDescriptor = {
  id: BlockingGateId;
  /** Lower number = higher priority. */
  priority: number;
  /** Short label for the sticky bar. */
  label: string;
  /** Whether the gate can hide behind the sticky bar while the user edits a sheet. */
  deferrable: boolean;
};

export const BLOCKING_GATE_PRIORITY: Record<BlockingGateId, number> = {
  "panic-alert": 0,
  "shift-end": 1,
  "break-active": 2,
  "break-notice": 3,
  "briefing-forced-read": 4,
};

export const BLOCKING_GATE_LABEL: Record<BlockingGateId, string> = {
  "panic-alert": "Panic alert",
  "shift-end": "Shift end required",
  "break-active": "Break in progress",
  "break-notice": "Break due",
  "briefing-forced-read": "Unread briefing",
};

type GateMap = Partial<Record<BlockingGateId, BlockingGateDescriptor>>;

/** Stable empty queue reference — must be reused when no gates are registered (React snapshot safety). */
export const EMPTY_BLOCKING_GATE_QUEUE: BlockingGateDescriptor[] = [];

export function buildBlockingGateQueue(gates: GateMap): BlockingGateDescriptor[] {
  const sorted = sortGatesByPriority(gates);
  return sorted.length === 0 ? EMPTY_BLOCKING_GATE_QUEUE : sorted;
}

export function sortGatesByPriority(gates: GateMap): BlockingGateDescriptor[] {
  return Object.values(gates)
    .filter((g): g is BlockingGateDescriptor => g != null)
    .toSorted((a, b) => a.priority - b.priority);
}

export function selectActiveGateId(gates: GateMap): BlockingGateId | null {
  let active: BlockingGateDescriptor | null = null;
  for (const gate of Object.values(gates)) {
    if (!gate) continue;
    if (active === null || gate.priority < active.priority) {
      active = gate;
    }
  }
  return active?.id ?? null;
}
